<template>
  <div class="staff-transfer-history">
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 resource-title">Staff Campus Transfers</h5>
          <small class="text-muted">{{ pageSubtitle }}</small>
        </div>
        <div class="d-flex flex-wrap align-items-center sth-view-tabs">
          <div class="sth-segment mb-50" role="tablist">
            <button
              type="button"
              role="tab"
              class="sth-segment__btn"
              :class="{ 'is-active': viewTab === 'records' }"
              @click="setViewTab('records')"
            >
              Transfer Record
            </button>
            <button
              type="button"
              role="tab"
              class="sth-segment__btn"
              :class="{ 'is-active': viewTab === 'history' }"
              @click="setViewTab('history')"
            >
              History
            </button>
          </div>
          <b-button
            v-if="viewTab === 'records' && rights.add"
            variant="gradient-primary"
            class="action-btn-filter add-sheet-btn text-nowrap mb-50 ml-50"
            @click="openRecordModal()"
          >
            <feather-icon icon="PlusIcon" class="mr-50" />
            <span>Add Record</span>
          </b-button>
          <b-button
            v-if="viewTab === 'history' && rights.view"
            variant="outline-primary"
            class="action-btn-filter add-sheet-btn text-nowrap mb-50 ml-50"
            @click="openPrintModal"
          >
            <feather-icon icon="PrinterIcon" class="mr-50" />
            <span>Print Report</span>
          </b-button>
        </div>
      </div>

      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="page-size-wrapper">
          <label class="page-size-label">Show:</label>
          <v-select
            v-model="perPage"
            :options="pageSizeOptions"
            :reduce="(option) => option.value"
            label="label"
            :clearable="false"
            :searchable="false"
            append-to-body
            class="page-size-select-compact"
            @input="onPageSizeChange"
          />
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="loadCurrentTab(false)"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div
            class="filter-fields-group d-flex align-items-center flex-grow-1"
            :class="
              isHeadOffice
                ? 'filter-fields-group--cols-4'
                : 'filter-fields-group--cols-3'
            "
          >
            <b-form-input
              v-model="searchQuery"
              class="filter-search-input filter-field-item"
              :placeholder="searchPlaceholder"
              @input="onSearchInput"
              @keyup.enter="onSearchEnter"
            />

            <v-select
              v-if="isHeadOffice"
              v-model="filters.campusID"
              :options="campusOptions"
              :clearable="false"
              label="branch"
              :reduce="(val) => val.id"
              placeholder="Select campus"
              append-to-body
              class="filter-control filter-field-item"
              @input="loadCurrentTab(true)"
            />

            <div class="filter-date-wrap filter-field-item">
              <flat-pickr
                v-model="filters.dtFrom"
                class="form-control filter-control filter-date-item"
                :config="filterDateConfig"
                placeholder="From date"
                @on-change="onFilterDateChange"
              />
            </div>

            <div class="filter-date-wrap filter-field-item">
              <flat-pickr
                v-model="filters.dtTo"
                class="form-control filter-control filter-date-item"
                :config="filterDateConfig"
                placeholder="To date"
                @on-change="onFilterDateChange"
              />
            </div>
          </div>

          <b-button
            v-if="hasActiveFilters"
            class="action-btn-filter filter-clear-btn mx-50"
            v-b-tooltip.hover.top
            title="Clear filters"
            @click="clearFilters"
          >
            <span class="filter-clear-x" aria-hidden="true">×</span>
          </b-button>
        </b-col>
      </b-row>

      <div class="sth-table-wrap">
        <b-table
          class="planner-list-table mt-1"
          :items="tableRows"
          :fields="activeFields"
          :busy="listLoading"
          show-empty
          :responsive="false"
          hover
          small
        >
          <template #empty>
            <h3 v-if="!listLoading" class="mt-1 mb-1 text-center">
              {{ emptyMessage }}
            </h3>
          </template>

          <template #table-busy>
            <div class="list-skeleton">
              <div
                v-for="n in 6"
                :key="'skeleton-' + n"
                class="list-skeleton__row"
                :style="{
                  gridTemplateColumns: `repeat(${activeFields.length}, minmax(0, 1fr))`,
                }"
              >
                <div
                  v-for="(field, index) in activeFields"
                  :key="'skeleton-cell-' + n + '-' + index"
                  class="list-skeleton__cell"
                >
                  <div
                    class="list-skeleton__bar"
                    :style="{ width: skeletonBarWidth(index) }"
                  ></div>
                </div>
              </div>
            </div>
          </template>

          <template #cell(sno)="{ index }">
            {{ (currentPage - 1) * perPage + index + 1 }}
          </template>

          <template #cell(staff)="{ item }">
            <div class="sth-staff-name">{{ item.name || "—" }}</div>
          </template>

          <template #cell(attachment)="{ item }">
            <b-badge
              v-if="attachmentCount(item.attachment) > 0"
              variant="light-primary"
              class="cursor-pointer"
              @click="openFirstAttachment(item.attachment)"
            >
              <feather-icon icon="PaperclipIcon" size="12" class="mr-25" />
              {{ attachmentCount(item.attachment) }}
            </b-badge>
            <span v-else class="text-muted">—</span>
          </template>

          <template #head(actions)="data">
            <div class="text-center w-100">
              <span>{{ data.label }}</span>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div
              v-if="viewTab === 'records'"
              class="table-actions-cell"
            >
              <b-button
                variant="gradient-primary"
                class="action-btn-table mr-1"
                v-b-tooltip.hover.top="'View'"
                @click="openViewRecord(item)"
              >
                <feather-icon icon="EyeIcon" />
              </b-button>
              <b-button
                v-if="rights.edit"
                variant="gradient-primary"
                class="action-btn-table mr-1"
                v-b-tooltip.hover.top="'Edit'"
                @click="openRecordModal(item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                class="action-btn-outline delete-btn"
                v-b-tooltip.hover.top="'Delete'"
                @click="deleteTransferRecord(item)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell(campusMove)="{ item }">
            <div
              v-if="campusFrom(item) || campusTo(item)"
              class="sth-campus-move"
            >
              <span class="sth-campus-from">{{ campusFrom(item) || "—" }}</span>
              <span class="sth-campus-sep">to</span>
              <span class="sth-campus-to">{{ campusTo(item) || "—" }}</span>
            </div>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell(transferDate)="{ item }">
            <span class="sth-date-main">{{
              formatDatePart(item.transferDate)
            }}</span>
          </template>

          <template #cell(notes)="{ item }">
            <span class="sth-notes" :title="notesText(item)">
              {{ notesText(item) || "—" }}
            </span>
          </template>

          <template #cell(recordedBy)="{ item }">
            <span class="sth-by">{{
              viewTab === "records"
                ? item.metaCreatedBy || "—"
                : item.metaTransferredBy || "—"
            }}</span>
          </template>
        </b-table>
      </div>
    </b-card>

    <b-modal
      v-model="showRecordModal"
      :title="recordEditId ? 'Edit Transfer Record' : 'Add Transfer Record'"
      centered
      no-close-on-backdrop
      size="lg"
      modal-class="staff-transfer-record-modal"
      @hidden="resetRecordForm"
      @shown="onRecordModalShown"
    >
      <b-form class="sth-record-form" @submit.prevent>
        <b-row>
          <b-col md="6">
            <b-form-group label="From campus *">
              <v-select
                :key="'from-c-' + recordModalKey"
                v-model="recordForm.fromCampusID"
                :options="campusPickOptions"
                :reduce="(c) => Number(c.id)"
                label="branch"
                :clearable="false"
                placeholder="Select campus"
                class="sth-record-select w-100"
                @input="onFromCampusChange"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="To campus *">
              <v-select
                :key="'to-c-' + recordModalKey"
                v-model="recordForm.toCampusID"
                :options="campusPickOptions"
                :reduce="(c) => Number(c.id)"
                label="branch"
                :clearable="false"
                placeholder="Select campus"
                class="sth-record-select w-100"
                @input="onToCampusChange"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group label="Staff *">
              <v-select
                :key="'staff-' + recordModalKey + '-' + recordForm.fromCampusID"
                v-model="recordForm.staffID"
                :options="staffPoolForCampus"
                :reduce="(s) => Number(s.id)"
                label="label"
                :filterable="true"
                :clearable="false"
                :loading="staffSearchLoading"
                :disabled="!recordForm.fromCampusID"
                :placeholder="
                  recordForm.fromCampusID
                    ? staffPoolForCampus.length
                      ? 'Search or pick staff'
                      : 'Loading staff…'
                    : 'Select from campus first'
                "
                class="sth-record-select w-100"
                @input="onRecordStaffSelected"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="From designation">
              <v-select
                :key="'from-d-' + recordForm.fromCampusID"
                v-model="recordForm.designationID"
                :options="fromDesignationOptions"
                :reduce="(d) => Number(d.id)"
                label="name"
                :clearable="true"
                :disabled="!recordForm.fromCampusID"
                placeholder="Select designation"
                class="sth-record-select w-100"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="To designation">
              <v-select
                :key="'to-d-' + recordForm.toCampusID"
                v-model="recordForm.transferDesignationID"
                :options="toDesignationOptions"
                :reduce="(d) => Number(d.id)"
                label="name"
                :clearable="true"
                :disabled="!recordForm.toCampusID"
                placeholder="Select designation"
                class="sth-record-select w-100"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group label="Transfer notes">
              <b-form-textarea
                v-model.trim="recordForm.transferNotes"
                rows="3"
                placeholder="Reason or notes"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group label="Transfer date">
              <div class="filter-date-wrap">
                <flat-pickr
                  v-model="recordForm.transferDate"
                  class="form-control filter-date-item"
                  :config="filterDateConfig"
                  placeholder="Transfer date"
                />
              </div>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group label="Attachments">
              <div class="sth-attachment-grid">
                <div
                  class="sth-attachment-tile sth-attachment-tile--add"
                  @click="
                    !attachmentUploading &&
                      $refs.transferRecordFile.click()
                  "
                >
                  <b-spinner
                    v-if="attachmentUploading"
                    small
                    variant="primary"
                  />
                  <feather-icon
                    v-else
                    icon="PlusIcon"
                    size="28"
                    class="text-primary"
                  />
                  <span class="sth-attachment-label">Add files</span>
                </div>
                <div
                  v-for="(doc, index) in attachDocs"
                  :key="'tr-att-' + index + '-' + doc.url"
                  class="sth-attachment-tile"
                  @click="openAttachment(doc.url)"
                >
                  <b-avatar
                    size="22"
                    variant="primary"
                    class="sth-attachment-remove"
                    @click.stop="removeAttachDoc(index)"
                  >
                    <feather-icon icon="XIcon" size="14" />
                  </b-avatar>
                  <feather-icon icon="FileTextIcon" size="26" class="text-primary" />
                  <span
                    class="sth-attachment-label text-truncate"
                    :title="docLabel(doc, index)"
                  >
                    {{ docLabel(doc, index) }}
                  </span>
                </div>
              </div>
              <input
                ref="transferRecordFile"
                type="file"
                hidden
                multiple
                accept="image/*,.pdf,.doc,.docx"
                @change="onRecordAttachmentFiles"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showRecordModal = false">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="recordSaving"
          @click="saveTransferRecord"
        >
          <b-spinner v-if="recordSaving" small class="mr-50" />
          Save
        </b-button>
      </template>
    </b-modal>

    <b-modal
      v-model="showViewRecordModal"
      title="Transfer Record Details"
      centered
      size="lg"
      header-bg-variant="light"
      header-text-variant="dark"
      modal-class="staff-transfer-view-modal"
    >
      <div v-if="viewRecordRow" class="view-modal-content">
        <div class="info-section">
          <h6 class="section-title">
            <feather-icon icon="UserIcon" class="mr-1" />
            Staff
          </h6>
          <div class="info-grid">
            <div class="info-item">
              <label>Staff name</label>
              <div class="info-value">{{ viewRecordRow.name || "—" }}</div>
            </div>
            <div v-if="viewRecordRow.departmentName" class="info-item">
              <label>Department</label>
              <div class="info-value">
                {{ viewRecordRow.departmentName }}
              </div>
            </div>
          </div>
        </div>

        <div class="info-section">
          <h6 class="section-title">
            <feather-icon icon="MapPinIcon" class="mr-1" />
            Campus move
          </h6>
          <div class="info-grid">
            <div class="info-item">
              <label>From campus</label>
              <div class="info-value">
                {{ campusFrom(viewRecordRow) || "—" }}
              </div>
            </div>
            <div class="info-item">
              <label>To campus</label>
              <div class="info-value">
                {{ campusTo(viewRecordRow) || "—" }}
              </div>
            </div>
            <div class="info-item">
              <label>From designation</label>
              <div class="info-value">
                {{ viewDesignationLabel(viewRecordRow, "from") }}
              </div>
            </div>
            <div class="info-item">
              <label>To designation</label>
              <div class="info-value">
                {{ viewDesignationLabel(viewRecordRow, "to") }}
              </div>
            </div>
          </div>
        </div>

        <div class="info-section">
          <h6 class="section-title">
            <feather-icon icon="CalendarIcon" class="mr-1" />
            Transfer
          </h6>
          <div class="info-grid">
            <div class="info-item">
              <label>Transfer date</label>
              <div class="info-value">
                {{ formatDatePart(viewRecordRow.transferDate) }}
              </div>
            </div>
          </div>
          <div class="info-item full-width mt-1">
            <label>Transfer notes</label>
            <div class="info-value notes-text">
              {{ viewRecordRow.transferNotes || "—" }}
            </div>
          </div>
          <div class="info-item full-width mt-1">
            <label>Attachments</label>
            <div
              v-if="viewRecordAttachmentDocs.length"
              class="attachment-view-list"
            >
              <div
                v-for="(doc, index) in viewRecordAttachmentDocs"
                :key="`${doc.url}-${index}`"
                class="attachment-view-item"
                @click="openAttachment(doc.url)"
              >
                <div class="attachment-view-doc">
                  <feather-icon icon="FileTextIcon" size="22" class="text-primary" />
                </div>
                <span class="attachment-view-name text-truncate">{{
                  docLabel(doc, index)
                }}</span>
              </div>
            </div>
            <div v-else class="info-value">—</div>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showViewRecordModal = false">
          <feather-icon icon="XIcon" class="mr-1" />
          Close
        </b-button>
      </template>
    </b-modal>

    <b-modal
      v-model="showPrintModal"
      title="Print Staff Campus Transfer"
      centered
      no-close-on-backdrop
      body-class="staff-transfer-print-modal-body"
      @hidden="resetPrintForm"
      @shown="onPrintModalShown"
    >
      <b-form @submit.prevent>
        <b-row>
          <b-col v-if="isHeadOffice" md="12">
            <b-form-group label="Campus" label-for="staff-transfer-print-campus">
              <v-select
                id="staff-transfer-print-campus"
                v-model="printForm.campusID"
                :options="campusOptions"
                :clearable="false"
                label="branch"
                :reduce="(val) => val.id"
                placeholder="Select campus"
                append-to-body
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="From Date"
              label-for="staff-transfer-print-from"
            >
              <flat-pickr
                :key="'print-from-' + printPickerKey"
                id="staff-transfer-print-from"
                v-model="printForm.dtFrom"
                :config="modalDateConfig"
                class="form-control"
                placeholder="From date"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="To Date" label-for="staff-transfer-print-to">
              <flat-pickr
                :key="'print-to-' + printPickerKey"
                id="staff-transfer-print-to"
                v-model="printForm.dtTo"
                :config="modalDateConfig"
                class="form-control"
                placeholder="To date"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showPrintModal = false">
          Cancel
        </b-button>
        <b-button variant="primary" @click="goToPrint">
          <feather-icon icon="PrinterIcon" class="mr-50" />
          Print Preview
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BPagination,
  BRow,
  BCol,
  BTable,
  BModal,
  BSpinner,
  BAvatar,
  BBadge,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import Swal from "sweetalert2";
import { mapActions } from "vuex";
import {
  uploadToMySkool,
  buildUploadedAttachment,
  parseDocUrls,
  getDocumentDisplayName,
} from "@/utils/gdriveUpload";

const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
import {
  buildStaffTransferPaginationUrl,
  buildStaffTransferRecordPaginationUrl,
  campusFromLabel,
  campusToLabel,
  formatCampusTransferDateTime,
} from "@/utils/campusTransferReport";

export default {
  name: "StaffTransferHistory",
  components: {
    BCard,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BPagination,
    BRow,
    BCol,
    BTable,
    BModal,
    BSpinner,
    BAvatar,
    BBadge,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    const appendToBody =
      typeof document !== "undefined" ? document.body : undefined;

    return {
      rights: {
        view: false,
        add: false,
        edit: false,
        delete: false,
      },
      listLoading: true,
      rows: [],
      totalCount: 0,
      currentPage: 1,
      perPage: 20,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      campuses: [],
      isHeadOffice: null,
      searchQuery: "",
      showPrintModal: false,
      printPickerKey: 0,
      printForm: {
        campusID: 0,
        dtFrom: "",
        dtTo: "",
      },
      filterDateConfig: {
        dateFormat: "Y-m-d",
        allowInput: true,
        appendTo: appendToBody,
      },
      modalDateConfig: {
        dateFormat: "Y-m-d",
        allowInput: true,
        static: false,
        appendTo: appendToBody,
      },
      filters: {
        campusID: 0,
        dtFrom: "",
        dtTo: "",
      },
      viewTab: "records",
      searchDebounceTimer: null,
      showRecordModal: false,
      showViewRecordModal: false,
      viewRecordRow: null,
      recordEditId: 0,
      recordSaving: false,
      attachmentUploading: false,
      staffSearchLoading: false,
      attachDocs: [],
      designationByCampus: {},
      designationSettingsLoaded: false,
      recordModalKey: 0,
      staffPoolForCampus: [],
      editRowSnapshot: null,
      recordForm: {
        id: 0,
        staffID: null,
        fromCampusID: null,
        toCampusID: null,
        designationID: null,
        transferDesignationID: null,
        transferDate: "",
        transferNotes: "",
        attachment: "",
      },
      recordFields: [
        { key: "sno", label: "#", thStyle: { width: "48px" } },
        { key: "staff", label: "Staff" },
        { key: "campusMove", label: "Campus Move" },
        { key: "transferDate", label: "Transfer Date" },
        {
          key: "notes",
          label: "Notes",
          thClass: "sth-notes-cell",
          tdClass: "sth-notes-cell",
        },
        { key: "attachment", label: "File", thStyle: { width: "64px" } },
        { key: "actions", label: "Actions", thStyle: { width: "148px" } },
      ],
      historyFields: [
        { key: "sno", label: "#", thStyle: { width: "48px" } },
        { key: "staff", label: "Staff" },
        { key: "campusMove", label: "Campus Move" },
        { key: "transferDate", label: "Transfer Date" },
        {
          key: "notes",
          label: "Notes / Details",
          thClass: "sth-notes-cell",
          tdClass: "sth-notes-cell",
        },
        { key: "recordedBy", label: "Transferred By" },
      ],
    };
  },
  computed: {
    pageSubtitle() {
      return this.viewTab === "records"
        ? "Manual transfer records — add, edit, and track campus moves"
        : "Automatic history from Staff Transfer — print report from here";
    },
    searchPlaceholder() {
      return this.viewTab === "records"
        ? "Search by name..."
        : "Search staff, notes, campus...";
    },
    emptyMessage() {
      return this.viewTab === "records"
        ? "No transfer records found"
        : "No automatic transfer history found";
    },
    activeFields() {
      return this.viewTab === "records"
        ? this.recordFields
        : this.historyFields;
    },
    campusPickOptions() {
      return this.campuses
        .filter((c) => Number(c.id) > 0)
        .map((c) => ({ ...c, id: Number(c.id) }));
    },
    fromDesignationOptions() {
      return this.designationOptionsForCampus(this.recordForm.fromCampusID);
    },
    toDesignationOptions() {
      return this.designationOptionsForCampus(this.recordForm.toCampusID);
    },
    campusOptions() {
      return [{ id: 0, branch: "All Campuses" }, ...this.campuses];
    },
    hasActiveFilters() {
      const campusChanged =
        this.isHeadOffice && Number(this.filters.campusID) > 0;
      return !!(
        campusChanged ||
        this.filters.dtFrom ||
        this.filters.dtTo ||
        this.searchQuery
      );
    },
    displayedRange() {
      if (!this.totalCount) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    recordFilteredRows() {
      return this.filterRowsBySearchText(this.rows);
    },
    historyFilteredRows() {
      return this.filterRowsBySearchText(this.rows, true);
    },
    tableRows() {
      return this.viewTab === "records"
        ? this.recordFilteredRows
        : this.historyFilteredRows;
    },
    viewRecordAttachmentDocs() {
      if (!this.viewRecordRow) return [];
      return parseDocUrls(this.viewRecordRow.attachment || "");
    },
  },
  async created() {
    if (!this.initRights()) return;

    await this.loadHeadOfficeSetting();
    if (this.isHeadOffice) {
      this.filters.campusID = 0;
    } else {
      this.filters.campusID = Number(this.$store.state.userData.cId) || 0;
    }
    await this.loadCampuses();
    await this.loadCurrentTab(true);
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
  },
  watch: {
    showRecordModal(open) {
      if (open && Number(this.recordForm.fromCampusID)) {
        this.loadStaffForFromCampus();
      }
    },
  },
  methods: {
    ...mapActions({ get: "get", post: "post", delete: "delete" }),

    initRights() {
      const right = {};
      (this.$store.state.menu || []).forEach((el) => {
        (el.children || []).forEach((ch) => {
          if (!ch || !ch.route) return;
          right[ch.route] = {
            view: !!ch.view,
            add: !!ch.add,
            edit: !!ch.edit,
            delete: !!ch.delete,
            assignSections: ch.assignSections,
          };
        });
      });
      this.$store.commit("setRights", right);

      // Prefer this page's menu rights; fall back to Staff Transfer if history
      // is not yet configured as its own menu item.
      const routeRights =
        this.$store.state.rights[this.$route.name] ||
        this.$store.state.rights["staff-transfer"] ||
        null;

      if (!routeRights || !routeRights.view) {
        this.listLoading = false;
        this.$router.replace({ name: "misc-not-authorized" });
        return false;
      }

      this.rights = routeRights;
      return true;
    },

    skeletonBarWidth(index) {
      const widths = ["42%", "72%", "58%", "65%", "80%", "70%"];
      return widths[index % widths.length];
    },

    campusFrom(item) {
      return campusFromLabel(item);
    },

    campusTo(item) {
      return campusToLabel(item);
    },

    notesText(item) {
      if (
        item.transferNotes &&
        item.details &&
        item.details !== item.transferNotes
      ) {
        return `${item.transferNotes} — ${item.details}`;
      }
      return item.transferNotes || item.details || "";
    },

    formatDatePart(value) {
      return formatCampusTransferDateTime(value).date || "—";
    },

    formatTimePart(value) {
      return formatCampusTransferDateTime(value).time || "";
    },

    sliceDate(value) {
      if (!value) return "";
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString().slice(0, 10);
      }
      const raw = String(value).trim();
      if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return "";
      return d.toISOString().slice(0, 10);
    },

    resolveUserID() {
      return (
        Number(this.$store.state.userData.userID) ||
        Number(this.$store.state.userData.id) ||
        0
      );
    },

    resolveCampusFilterID() {
      return this.isHeadOffice
        ? this.filters.campusID
        : Number(this.$store.state.userData.cId) || this.filters.campusID;
    },

    async setViewTab(tab) {
      if (this.viewTab === tab) return;
      this.viewTab = tab;
      this.searchQuery = "";
      this.currentPage = 1;
      this.listLoading = true;
      this.rows = [];
      await this.loadCurrentTab(true);
    },

    async loadCurrentTab(resetPage = false) {
      if (this.viewTab === "records") {
        await this.loadTransferRecords(resetPage);
      } else {
        await this.loadAutoTransferHistory(resetPage);
      }
    },

    onSearchInput() {
      if (this.viewTab !== "records") return;
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.loadTransferRecords(true);
      }, 400);
    },

    onSearchEnter() {
      if (this.viewTab === "records") {
        if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
        this.loadTransferRecords(true);
      }
    },

    rowSearchHaystack(item, includeHistoryFields = false) {
      if (!includeHistoryFields) {
        return String(item.name || item.staffName || item.Name || "")
          .trim()
          .toLowerCase();
      }
      const parts = [
        item.name,
        item.staffID,
        item.empCode,
        item.employeeCode,
        item.designation,
        item.departmentName,
        item.metaFromCampusName,
        item.metaToCampusName,
        this.campusFrom(item),
        this.campusTo(item),
        item.transferNotes,
        item.details,
        item.metaTransferredBy,
      ];
      return parts
        .filter((p) => p != null && String(p).trim() !== "")
        .join(" ")
        .toLowerCase();
    },

    filterRowsBySearchText(list, includeHistoryFields = false) {
      const q = String(this.searchQuery || "")
        .trim()
        .toLowerCase();
      if (!q) return list;
      return (list || []).filter((item) =>
        this.rowSearchHaystack(item, includeHistoryFields).includes(q)
      );
    },

    todayDateString() {
      return new Date().toISOString().slice(0, 10);
    },

    onFilterDateChange() {
      this.filters.dtFrom = this.sliceDate(this.filters.dtFrom);
      this.filters.dtTo = this.sliceDate(this.filters.dtTo);
      this.loadCurrentTab(true);
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadCurrentTab(false);
    },

    openPrintModal() {
      this.printForm = {
        campusID: this.isHeadOffice
          ? Number(this.filters.campusID) || 0
          : Number(this.$store.state.userData.cId) || 0,
        dtFrom: this.filters.dtFrom || "",
        dtTo: this.filters.dtTo || "",
      };
      this.printPickerKey += 1;
      this.showPrintModal = true;
    },

    onPrintModalShown() {
      // Remount pickers after modal is fully in DOM so calendars attach correctly
      this.printPickerKey += 1;
    },

    resetPrintForm() {
      this.printForm = {
        campusID: this.isHeadOffice
          ? 0
          : Number(this.$store.state.userData.cId) || 0,
        dtFrom: "",
        dtTo: "",
      };
    },

    goToPrint() {
      const dtFrom = this.sliceDate(this.printForm.dtFrom);
      const dtTo = this.sliceDate(this.printForm.dtTo);

      if (dtFrom && dtTo && new Date(dtFrom) > new Date(dtTo)) {
        this.$bvToast.toast("From date cannot be after To date.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
        return;
      }

      const campusID = this.isHeadOffice
        ? Number(this.printForm.campusID) || 0
        : Number(this.$store.state.userData.cId) || 0;

      const query = {
        db: this.$store.state.userData.db,
      };

      if (campusID > 0) {
        query.campusID = campusID;
      }

      if (dtFrom) query.dtFrom = dtFrom;
      if (dtTo) query.dtTo = dtTo;

      const routeData = this.$router.resolve({
        name: "staff-campus-transfer-report",
        query,
      });
      window.open(routeData.href, "_blank");
      this.showPrintModal = false;
    },

    clearFilters() {
      this.filters.campusID = this.isHeadOffice
        ? 0
        : Number(this.$store.state.userData.cId) || 0;
      this.filters.dtFrom = "";
      this.filters.dtTo = "";
      this.searchQuery = "";
      this.loadCurrentTab(true);
    },

    displayEmpCode(code) {
      const v = String(code ?? "").trim();
      if (!v || v === "0") return "";
      return v;
    },

    staffOptionLabel(staff) {
      if (!staff) return "";
      const name = String(staff.name || "").trim() || "Staff";
      const dept = String(staff.designation || staff.departmentName || "").trim();
      return dept ? `${name} — ${dept}` : name;
    },

    async openViewRecord(row) {
      this.viewRecordRow = row ? { ...row } : null;
      if (row?.fromCampusID) {
        await this.ensureDesignationsForCampus(Number(row.fromCampusID));
      }
      if (row?.toCampusID) {
        await this.ensureDesignationsForCampus(Number(row.toCampusID));
      }
      this.showViewRecordModal = true;
    },

    viewDesignationLabel(row, which) {
      if (!row) return "—";
      const id =
        which === "from"
          ? Number(row.designationID)
          : Number(row.transferDesignationID);
      const campusId =
        which === "from"
          ? Number(row.fromCampusID)
          : Number(row.toCampusID);
      if (id > 0) {
        const options = this.designationOptionsForCampus(campusId);
        const match = options.find((d) => Number(d.id) === id);
        if (match?.name) return match.name;
      }
      if (which === "from" && row.designation) return row.designation;
      return "—";
    },

    mapStaffOption(s) {
      const id = Number(s.id);
      return {
        ...s,
        id,
        label: this.staffOptionLabel(s),
      };
    },

    parseStaffListResponse(response) {
      if (!response) return [];
      if (Array.isArray(response.data)) return response.data;
      if (Array.isArray(response)) return response;
      if (response.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      return [];
    },

    async loadStaffForFromCampus() {
      const campusId = Number(this.recordForm.fromCampusID);
      if (!campusId) {
        this.staffPoolForCampus = [];
        return;
      }
      this.staffSearchLoading = true;
      try {
        const url =
          `${this.$store.state.domain}staff/LoadDataPagination?db=` +
          `${encodeURIComponent(this.$store.state.userData.db)}` +
          `&cId=${campusId}&dptID=0&status=present&page=1&pageSize=1000`;

        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        const list = this.parseStaffListResponse(response);
        this.staffPoolForCampus = list
          .filter((s) => s && s.id != null)
          .map((s) => this.mapStaffOption(s));
      } catch (e) {
        console.error("Load staff for campus failed", e);
        this.staffPoolForCampus = [];
        this.$bvToast.toast("Could not load staff for the selected campus.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.staffSearchLoading = false;
      }
    },

    ensureStaffOptionInPool(staffRow) {
      if (!staffRow || !staffRow.staffID) return;
      const id = Number(staffRow.staffID);
      if (this.staffPoolForCampus.some((s) => s.id === id)) return;
      this.staffPoolForCampus.unshift(
        this.mapStaffOption({
          id,
          name: staffRow.name,
          empCode: staffRow.empCode,
          designation: staffRow.designation,
          campusID: staffRow.fromCampusID,
        })
      );
    },

    onRecordStaffSelected(staffId) {
      const staff = this.staffPoolForCampus.find(
        (s) => Number(s.id) === Number(staffId)
      );
      if (!staff) return;
      this.matchDesignationFromStaff(staff, "from");
    },

    async onFromCampusChange(campusId) {
      const id = Number(campusId) || 0;
      this.recordForm.staffID = null;
      await this.ensureDesignationsForCampus(id);
      await this.loadStaffForFromCampus();
      if (
        this.recordForm.designationID &&
        !this.fromDesignationOptions.some(
          (d) => Number(d.id) === Number(this.recordForm.designationID)
        )
      ) {
        this.recordForm.designationID = null;
      }
    },

    async onToCampusChange(campusId) {
      const id = Number(campusId) || 0;
      await this.ensureDesignationsForCampus(id);
      if (
        this.recordForm.transferDesignationID &&
        !this.toDesignationOptions.some(
          (d) => Number(d.id) === Number(this.recordForm.transferDesignationID)
        )
      ) {
        this.recordForm.transferDesignationID = null;
      }
    },

    designationOptionsForCampus(campusId) {
      const id = Number(campusId) || 0;
      if (!id) return [];
      return this.designationByCampus[id] || [];
    },

    async ensureDesignationsForCampus(campusId) {
      const id = Number(campusId) || 0;
      if (!id || this.designationByCampus[id]) return;
      await this.loadDesignationSettingsOnce();
      if (this.designationByCampus[id]) return;
      const fallback = this.designationByCampus[0] || [];
      if (fallback.length) {
        this.$set(this.designationByCampus, id, fallback);
      }
    },

    async loadDesignationSettingsOnce() {
      if (this.designationSettingsLoaded) return;
      try {
        const response = await this.post({
          url:
            this.$store.state.domain +
            "Settings/getSettingsAll?db=" +
            this.$store.state.userData.db,
          body: ["staff-designations"],
          context: this,
          token: this.$store.state.userData.token,
          subjects: true,
        });
        let settingsArray = [];
        if (
          response &&
          response.status === "success" &&
          Array.isArray(response.data)
        ) {
          settingsArray = response.data;
        } else if (Array.isArray(response)) {
          settingsArray = response;
        }
        settingsArray
          .filter((s) => s && s.key === "staff-designations")
          .forEach((setting) => {
            const cId = Number(setting.campusID) || 0;
            const options = this.normalizeDesignations(setting.value);
            if (options.length) {
              this.$set(this.designationByCampus, cId, options);
            }
          });
        if (!Object.keys(this.designationByCampus).length) {
          const any = settingsArray.find((s) => s.key === "staff-designations");
          if (any) {
            this.$set(
              this.designationByCampus,
              0,
              this.normalizeDesignations(any.value)
            );
          }
        }
      } catch (e) {
        console.error("Failed to load designation settings", e);
      } finally {
        this.designationSettingsLoaded = true;
      }
    },

    normalizeDesignations(rawValue) {
      const toOption = (item, index) => {
        if (typeof item === "string") {
          const name = item.trim();
          if (name.includes("|")) {
            const [n, d] = name.split("|");
            return {
              id: index + 1,
              name: (n || "").trim(),
              departmentID: Number(d) || 0,
            };
          }
          return { id: index + 1, name, departmentID: 0 };
        }
        const explicitId = Number(item.id ?? item.ID ?? 0);
        return {
          id: explicitId > 0 ? explicitId : index + 1,
          name: String(item.name || item.Name || "").trim(),
          departmentID:
            Number(
              item.departmentID ??
                item.departmentId ??
                item.DepartmentID ??
                0
            ) || 0,
        };
      };

      if (!rawValue) return [];
      let value = rawValue;
      if (typeof value === "string") {
        value = value.replace(/^\uFEFF/, "").trim();
      }
      if (Array.isArray(value)) {
        return value.map(toOption).filter((o) => o.name);
      }
      if (typeof value === "string") {
        if (value.startsWith("[") || value.startsWith("{")) {
          try {
            let parsed = JSON.parse(value);
            if (typeof parsed === "string") parsed = JSON.parse(parsed);
            if (Array.isArray(parsed)) {
              return parsed.map(toOption).filter((o) => o.name);
            }
          } catch (e) {
            /* legacy */
          }
        }
        return value
          .split(",")
          .map((n) => n.trim())
          .filter(Boolean)
          .map((name, index) => toOption(name, index));
      }
      return [];
    },

    matchDesignationFromStaff(staff, which) {
      const label = String(staff.designation || "").trim().toLowerCase();
      if (!label) return;
      const campusId =
        which === "from"
          ? this.recordForm.fromCampusID
          : this.recordForm.toCampusID;
      const options = this.designationOptionsForCampus(campusId);
      const match = options.find(
        (d) => String(d.name || "").trim().toLowerCase() === label
      );
      if (!match) return;
      if (which === "from") this.recordForm.designationID = match.id;
      else this.recordForm.transferDesignationID = match.id;
    },

    openRecordModal(row) {
      this.editRowSnapshot = row && row.id ? row : null;
      if (row && row.id) {
        this.recordEditId = row.id;
        this.recordForm = {
          id: row.id,
          staffID: Number(row.staffID) || null,
          fromCampusID: Number(row.fromCampusID) || null,
          toCampusID: Number(row.toCampusID) || null,
          designationID: row.designationID ? Number(row.designationID) : null,
          transferDesignationID: row.transferDesignationID
            ? Number(row.transferDesignationID)
            : null,
          transferDate: this.sliceDate(row.transferDate) || "",
          transferNotes: row.transferNotes || "",
          attachment: row.attachment || "",
        };
        this.attachDocs = parseDocUrls(row.attachment || "");
      } else {
        this.recordEditId = 0;
        this.resetRecordForm();
      }
      this.recordModalKey += 1;
      this.showRecordModal = true;
      this.$nextTick(() => this.hydrateRecordModal());
    },

    async hydrateRecordModal() {
      const fromId = Number(this.recordForm.fromCampusID);
      const toId = Number(this.recordForm.toCampusID);
      const savedStaffId = this.recordForm.staffID;
      if (fromId) {
        await this.ensureDesignationsForCampus(fromId);
        await this.loadStaffForFromCampus();
        if (this.editRowSnapshot) {
          this.ensureStaffOptionInPool(this.editRowSnapshot);
        }
        if (savedStaffId) this.recordForm.staffID = Number(savedStaffId);
      }
      if (toId) await this.ensureDesignationsForCampus(toId);
    },

    onRecordModalShown() {
      const cId = Number(this.$store.state.userData.cId) || 0;
      if (!this.isHeadOffice && !this.recordEditId && !this.recordForm.fromCampusID) {
        this.recordForm.fromCampusID = cId;
        this.onFromCampusChange(cId);
      }
    },

    resetRecordForm() {
      this.recordEditId = 0;
      const cId = Number(this.$store.state.userData.cId) || 0;
      this.staffPoolForCampus = [];
      this.recordForm = {
        id: 0,
        staffID: null,
        fromCampusID: this.isHeadOffice ? null : cId,
        toCampusID: null,
        designationID: null,
        transferDesignationID: null,
        transferDate: this.todayDateString(),
        transferNotes: "",
        attachment: "",
      };
      this.attachDocs = [];
    },

    attachmentCount(value) {
      return this.parseAttachmentString(value).length;
    },

    parseAttachmentString(value) {
      if (!value) return [];
      return String(value)
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean);
    },

    syncAttachmentField() {
      this.recordForm.attachment = this.attachDocs
        .map((d) => d.url)
        .filter(Boolean)
        .join(",");
    },

    docLabel(doc, index) {
      return getDocumentDisplayName(doc.url, index);
    },

    removeAttachDoc(index) {
      this.attachDocs.splice(index, 1);
      this.syncAttachmentField();
    },

    openFirstAttachment(raw) {
      const urls = this.parseAttachmentString(raw);
      if (urls.length) this.openAttachment(urls[0]);
    },

    openAttachment(url) {
      if (!url) return;
      window.open(url, "_blank", "noopener,noreferrer");
    },

    async onRecordAttachmentFiles() {
      const input = this.$refs.transferRecordFile;
      if (!input || !input.files || !input.files.length) return;
      const files = Array.from(input.files);
      const accepted = [];
      const oversized = [];
      files.forEach((file) => {
        if (file.size > ATTACHMENT_MAX_BYTES) oversized.push(file.name);
        else accepted.push(file);
      });
      if (oversized.length) {
        this.$bvToast.toast(
          `Skipped (max 25MB): ${oversized.slice(0, 3).join(", ")}`,
          { title: "File too large", variant: "warning", solid: true }
        );
      }
      if (!accepted.length) {
        input.value = "";
        return;
      }
      this.attachmentUploading = true;
      try {
        const uploaded = await uploadToMySkool(accepted);
        (uploaded || []).forEach((item) => {
          const doc = buildUploadedAttachment(item);
          if (
            doc.url &&
            !this.attachDocs.some((existing) => existing.url === doc.url)
          ) {
            this.attachDocs.push(doc);
          }
        });
        this.syncAttachmentField();
      } catch (err) {
        this.$bvToast.toast("Failed to upload attachment(s).", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.attachmentUploading = false;
        input.value = "";
      }
    },

    async saveTransferRecord() {
      if (!this.recordForm.fromCampusID || !this.recordForm.toCampusID) {
        this.$bvToast.toast("Please select from and to campus.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
        return;
      }
      if (!this.recordForm.staffID) {
        this.$bvToast.toast("Please select a staff member.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
        return;
      }
      this.recordSaving = true;
      try {
        this.syncAttachmentField();
        const payload = {
          id: this.recordEditId || 0,
          staffID: Number(this.recordForm.staffID),
          fromCampusID: Number(this.recordForm.fromCampusID) || 0,
          toCampusID: Number(this.recordForm.toCampusID) || 0,
          designationID: Number(this.recordForm.designationID) || 0,
          transferDesignationID:
            Number(this.recordForm.transferDesignationID) || 0,
          transferDate: this.sliceDate(this.recordForm.transferDate) || null,
          transferNotes: this.recordForm.transferNotes || "",
          attachment: this.recordForm.attachment || null,
        };
        const userID = this.resolveUserID();
        const res = await this.post({
          url:
            this.$store.state.domain +
            "StaffTransferRecord/Save?db=" +
            this.$store.state.userData.db +
            (userID ? "&userID=" + userID : ""),
          body: payload,
          context: this,
          token: this.$store.state.userData.token,
          message: this.recordEditId
            ? "Transfer record updated."
            : "Transfer record added.",
        });
        if (res) {
          this.showRecordModal = false;
          this.loadTransferRecords(false);
        }
      } catch (error) {
        console.error("Save transfer record failed", error);
      } finally {
        this.recordSaving = false;
      }
    },

    async deleteTransferRecord(item) {
      const result = await Swal.fire({
        title: "Delete this record?",
        text: "This only removes the manual entry — staff campus is not changed.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete",
      });
      if (!result.isConfirmed) return;
      const userID = this.resolveUserID();
      try {
        const ok = await this.delete({
          url:
            this.$store.state.domain +
            "StaffTransferRecord/Delete?db=" +
            this.$store.state.userData.db +
            "&id=" +
            item.id +
            (userID ? "&userID=" + userID : ""),
          context: this,
          token: this.$store.state.userData.token,
          message: "Transfer record deleted.",
        });
        if (ok) this.loadTransferRecords(false);
      } catch (error) {
        console.error("Delete transfer record failed", error);
      }
    },

    async loadHeadOfficeSetting() {
      try {
        const res = await this.post({
          url:
            this.$store.state.domain +
            "Settings/getSettingsAll?db=" +
            this.$store.state.userData.db,
          body: ["head_office"],
          context: this,
          token: this.$store.state.userData.token,
          subjects: true,
        });
        this.isHeadOffice = (Array.isArray(res) ? res : []).find(
          (el) =>
            el.key == "head_office" &&
            el.campusID == this.$store.state.userData.cId
        );
      } catch (error) {
        console.error("Failed to load head office setting", error);
        this.isHeadOffice = null;
      }
    },

    async loadCampuses() {
      try {
        const response = await this.get({
          url:
            this.$store.state.domain +
            "campuses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        });
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : [];
        this.campuses = list.filter((c) => Number(c.id) > 0);
      } catch (error) {
        console.error("Failed to load campuses", error);
        this.campuses = [];
      }
    },

    async loadTransferRecords(resetPage = false) {
      if (this.viewTab !== "records") {
        this.listLoading = false;
        return;
      }
      if (resetPage) this.currentPage = 1;
      this.listLoading = true;
      this.rows = [];

      try {
        const url = buildStaffTransferRecordPaginationUrl({
          domain: this.$store.state.domain,
          db: this.$store.state.userData.db,
          campusID: this.resolveCampusFilterID(),
          page: this.currentPage,
          pageSize: this.perPage,
          search: this.searchQuery,
          dtFrom: this.filters.dtFrom,
          dtTo: this.filters.dtTo,
        });

        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        const payload = response || {};
        const data = Array.isArray(payload.data)
          ? payload.data
          : Array.isArray(payload)
          ? payload
          : [];

        this.rows = data;
        this.totalCount =
          Number(
            payload.totalCount ??
              payload.count ??
              payload.total ??
              data.length
          ) || 0;
      } catch (error) {
        console.error("Failed to load transfer records", error);
        this.rows = [];
        this.totalCount = 0;
        this.$bvToast.toast("Failed to load transfer records.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.listLoading = false;
      }
    },

    async loadAutoTransferHistory(resetPage = false) {
      if (this.viewTab !== "history") {
        this.listLoading = false;
        return;
      }
      if (resetPage) this.currentPage = 1;
      this.listLoading = true;
      this.rows = [];

      try {
        const url = buildStaffTransferPaginationUrl({
          domain: this.$store.state.domain,
          db: this.$store.state.userData.db,
          campusID: this.resolveCampusFilterID(),
          page: this.currentPage,
          pageSize: this.perPage,
          dtFrom: this.filters.dtFrom,
          dtTo: this.filters.dtTo,
        });

        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        const payload = response || {};
        const data = Array.isArray(payload.data)
          ? payload.data
          : Array.isArray(payload)
          ? payload
          : [];

        this.rows = data;
        this.totalCount =
          Number(
            payload.totalCount ??
              payload.count ??
              payload.total ??
              data.length
          ) || 0;
      } catch (error) {
        console.error("Failed to load staff transfer history", error);
        this.rows = [];
        this.totalCount = 0;
        this.$bvToast.toast("Failed to load staff transfer history.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.listLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../planner/plannerListTable.scss";

.staff-transfer-history {
  // Kill the native horizontal scrollbar / arrow buttons under the table
  .sth-table-wrap {
    width: 100%;
    overflow: visible;
  }

  ::v-deep .planner-list-table {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100%;
  }
}

.sth-staff-name {
  font-weight: 700;
  color: #2d1b69;
}

.sth-campus-move {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  line-height: 1.35;
}

.sth-campus-from {
  color: #9ca3af;
  text-decoration: line-through;
  font-weight: 600;
}

.sth-campus-sep {
  color: #94a3b8;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: lowercase;
}

.sth-campus-to {
  color: #059669;
  font-weight: 700;
}

.sth-date-stack {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.sth-date-main {
  font-weight: 700;
  color: #111827;
  font-size: 0.78rem;
}

.sth-date-time {
  font-size: 0.72rem;
  color: #6b7280;
}

.sth-notes {
  display: inline-block;
  max-width: min(280px, 28vw);
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.35;
  font-size: 0.875rem;
  color: #475569;
  vertical-align: middle;
}

::v-deep .planner-list-table td.sth-notes-cell {
  white-space: normal !important;
  vertical-align: middle !important;
  max-width: 280px;
}

::v-deep .planner-list-table th.sth-notes-cell {
  white-space: normal !important;
  vertical-align: middle !important;
}

.sth-by {
  font-size: 0.8rem;
  color: #4b5563;
  word-break: break-word;
}

.sth-view-tabs {
  gap: 0.35rem;
}

.sth-segment {
  display: inline-flex;
  padding: 3px;
  border-radius: 10px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
}

.sth-segment__btn {
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.4rem 0.85rem;
  border-radius: 8px;
  line-height: 1.2;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.sth-segment__btn.is-active {
  background: #7367f0;
  color: #fff;
  box-shadow: 0 2px 6px rgba(115, 103, 240, 0.35);
}

.sth-attachment-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.sth-attachment-tile {
  position: relative;
  width: 96px;
  min-height: 96px;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  cursor: pointer;
  background: #fafafa;
}

.sth-attachment-tile--add {
  border-style: dashed;
}

.sth-attachment-label {
  font-size: 0.68rem;
  margin-top: 0.35rem;
  max-width: 88px;
  text-align: center;
  color: #64748b;
}

.sth-attachment-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  cursor: pointer;
  z-index: 1;
}

.cursor-pointer {
  cursor: pointer;
}

.sth-record-form ::v-deep .form-group {
  margin-bottom: 0.85rem;
}

.sth-record-form ::v-deep .form-group > label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.35rem;
}

.sth-record-select ::v-deep .vs__dropdown-toggle {
  min-height: 38px;
  border-color: #d8d6de;
  padding: 4px 8px;
}

.sth-record-select ::v-deep .vs__selected {
  margin: 2px 0;
  padding: 0;
  color: #111827;
  font-size: 0.875rem;
  line-height: 1.35;
  opacity: 1;
}

.sth-record-select ::v-deep .vs__search {
  font-size: 0.875rem;
}

.table-actions-cell {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-width: 132px;
}

.view-modal-content {
  .info-section {
    margin-bottom: 1.75rem;

    .section-title {
      color: #2d1b69;
      font-weight: 700;
      font-size: 0.8125rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-bottom: 1rem;
      padding-bottom: 0.5rem;
      border-bottom: 2px solid #e0d8ff;
      display: flex;
      align-items: center;
    }

    .info-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem 1.25rem;

      @media (max-width: 768px) {
        grid-template-columns: 1fr;
      }
    }

    .info-item {
      &.full-width {
        grid-column: 1 / -1;
      }

      label {
        display: block;
        font-size: 0.72rem;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        margin-bottom: 0.4rem;
      }

      .info-value {
        font-size: 0.9rem;
        color: #2d1b69;
        font-weight: 500;
        padding: 0.65rem 0.85rem;
        background: linear-gradient(180deg, #fff 0%, #faf9ff 100%);
        border: 1px solid #e0d8ff;
        border-radius: 10px;
        min-height: 2.5rem;
        display: flex;
        align-items: center;

        &.notes-text {
          white-space: pre-wrap;
          word-break: break-word;
          align-items: flex-start;
          line-height: 1.5;
        }
      }
    }
  }
}

.attachment-view-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.attachment-view-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  max-width: 220px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #7367f0;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.12);
  }
}

.attachment-view-doc {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f5f3ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.attachment-view-name {
  font-size: 0.78rem;
  font-weight: 600;
  color: #2d1b69;
}

.filter-clear-x {
  font-size: 1.25rem;
  line-height: 1;
  font-weight: 700;
}
</style>

<style lang="scss">
/* Flatpickr calendars must sit above Bootstrap Vue modals */
.flatpickr-calendar {
  z-index: 11000 !important;
}
</style>
